import { useState, useEffect, useLayoutEffect } from 'react';
import { useUIState } from '../../contexts/UIStateContext';
import BaseDetailView from '../shared/BaseDetailView';
import DetailViewActionButtons from '../shared/DetailViewActionButtons';
import DetailsTab from './DetailsTab';
import SystemPromptTab from './SystemPromptTab';
import FunctionsTab from './FunctionsTab';
import NavigationArrow from '../shared/NavigationArrow';
import Badge from '../shared/Badge';
import EditSaveButtons from '../shared/EditSaveButtons';
import { useToggleField } from '../../hooks/useToggleField';
import { getSubTheme } from '../../constants/subThemes';
import { ICONS } from '../../constants/detailViewConstants';

// Arguments Tab Component
function ArgumentsTab({ configuration, parameters, setParameters, isEditMode, setIsEditMode, isSaving, setIsSaving, onUpdate }) {

  const handleCancel = () => {
    // Reset parameters from configuration
    if (configuration?.provider_specific_params) {
      try {
        const params = configuration.provider_specific_params;
        const paramArray = Object.entries(params).map(([name, value]) => ({
          name,
          value: typeof value === 'object' ? JSON.stringify(value) : String(value),
          type: typeof value
        }));
        setParameters(paramArray);
      } catch (error) {
        console.error('Failed to reset parameters:', error);
      }
    }
    setIsEditMode(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsedParams = {};
      parameters.forEach(param => {
        try {
          // Try to parse as JSON first
          parsedParams[param.name] = JSON.parse(param.value);
        } catch {
          // If not valid JSON, check the type and convert appropriately
          if (param.type === 'number') {
            parsedParams[param.name] = parseFloat(param.value);
          } else if (param.type === 'boolean') {
            parsedParams[param.name] = param.value === 'true';
          } else {
            parsedParams[param.name] = param.value;
          }
        }
      });

      const updates = {
        provider_specific_params: Object.keys(parsedParams).length > 0 ? parsedParams : null
      };

      await onUpdate?.(updates);
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to save provider parameters:', error);
      alert('Failed to save provider parameters');
    } finally {
      setIsSaving(false);
    }
  };

  const addParameter = () => {
    setParameters([...parameters, { name: '', value: '', type: 'string' }]);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Provider Arguments
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Define provider-specific parameters for this model configuration
          </p>
        </div>

        {/* Edit/Save/Cancel Buttons */}
        <EditSaveButtons
          isEditMode={isEditMode}
          isSaving={isSaving}
          onEdit={() => setIsEditMode(true)}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>

      {/* Parameters List */}
      {parameters.length === 0 ? (
        <div className="text-center py-12 rounded-lg" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
          <svg className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-disabled)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <p className="text-sm mb-4">No provider-specific parameters configured</p>
          {isEditMode && (
            <button
              onClick={addParameter}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg text-sm transition-all"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              Add Parameter
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {parameters.map((param, index) => (
            <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  {/* Parameter Name */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-muted)' }}>
                      Parameter Name
                    </label>
                    <input
                      type="text"
                      value={param.name}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const newParams = [...parameters];
                        newParams[index] = { ...newParams[index], name: e.target.value };
                        setParameters(newParams);
                      }}
                      className="w-full rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border-default)',
                        borderWidth: '1px',
                        color: 'var(--color-text-primary)'
                      }}
                      placeholder="e.g., top_p"
                    />
                  </div>

                  {/* Parameter Value */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-muted)' }}>
                      Value
                    </label>
                    <input
                      type="text"
                      value={param.value}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const newParams = [...parameters];
                        newParams[index] = { ...newParams[index], value: e.target.value };
                        setParameters(newParams);
                      }}
                      className="w-full rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border-default)',
                        borderWidth: '1px',
                        color: 'var(--color-text-primary)'
                      }}
                      placeholder="e.g., 0.9"
                    />
                  </div>
                </div>

                {/* Delete Button */}
                {isEditMode && (
                  <button
                    onClick={() => removeParameter(index)}
                    className="ml-4 p-2 rounded transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#ef4444';
                      e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Remove parameter"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          {isEditMode && (
            <button
              onClick={addParameter}
              className="w-full p-3 border border-dashed rounded-lg transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                borderColor: 'var(--color-border-default)',
                color: 'var(--color-text-muted)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                e.currentTarget.style.color = 'var(--color-text-muted)';
              }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Parameter
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ModelDetailView({ configId, configuration, onUpdate, onDelete, onRefreshConfiguration, aiOverview, onPrevious = null, onNext = null, theme = 'purple' }) {
  const { getAdminDetailTab, setAdminDetailTab } = useUIState();
  const [activeTab, setActiveTab] = useState('configuration');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Provider parameters state for Arguments tab
  const [parameters, setParameters] = useState([]);

  // Update tab when configuration changes (restore saved state)
  // Using useLayoutEffect to prevent flash of default tab before switching to saved tab
  useLayoutEffect(() => {
    if (configId) {
      const savedTab = getAdminDetailTab(configId);
      setActiveTab(savedTab);
    }
  }, [configId]);

  // Parse provider_specific_params into parameters array for Arguments tab
  useEffect(() => {
    if (configuration?.provider_specific_params) {
      try {
        const params = configuration.provider_specific_params;
        const paramArray = Object.entries(params).map(([name, value]) => ({
          name,
          value: typeof value === 'object' ? JSON.stringify(value) : String(value),
          type: typeof value
        }));
        setParameters(paramArray);
      } catch (error) {
        console.error('Failed to parse provider params:', error);
        setParameters([]);
      }
    } else {
      setParameters([]);
    }
    setIsEditMode(false);
  }, [configuration]);

  // Wrapper to persist active tab to UIState (per config)
  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    setAdminDetailTab(configId, tab);
  };

  // Use the job data from the configuration object (from API)
  const jobName = configuration?.job || 'Unknown';
  const modelType = configuration?.model_type || 'Unknown';

  const handleToggleActive = useToggleField(configuration, 'is_active', onUpdate, 'Failed to toggle active state');

  const handleDeleteConfiguration = async () => {
    try {
      await onDelete?.(configId);
    } catch (error) {
      console.error('Failed to delete configuration:', error);
      alert('Failed to delete configuration');
    }
  };

  const tabs = [
    { id: 'configuration', label: 'Details', shortLabel: 'Details', icon: ICONS.settings },
    { id: 'prompt', label: 'Instructions', shortLabel: 'Inst', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { id: 'arguments', label: 'Arguments', shortLabel: 'Arg', icon: 'M4 6h16M4 12h16M4 18h16' },
    { id: 'functions', label: 'Functions', shortLabel: 'Func', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' }
  ];

  // Header content with navigation arrows, title, pills, and connection status
  const headerContent = (
    <div className="flex items-start gap-4">
      {/* Navigation Arrow - Previous */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="previous"
          onClick={onPrevious}
          disabled={!onPrevious}
          label={onPrevious ? 'Previous model' : 'No previous model'}
          className="mt-1"
        />
      )}

      <div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>{configuration?.name || 'Unknown Configuration'}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Job Name Badge */}
          <Badge variant="gray">
            {jobName}
          </Badge>

          {/* Model Type Badge */}
          <Badge variant="gray">
            {modelType}
          </Badge>

          {/* Model Name Badge */}
          {configuration?.model_name && (
            <Badge variant="gray">
              {configuration.model_name}
            </Badge>
          )}
        </div>
      </div>

      {/* Navigation Arrow - Next */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="next"
          onClick={onNext}
          disabled={!onNext}
          label={onNext ? 'Next model' : 'No next model'}
          className="mt-1"
        />
      )}
    </div>
  );

  // Action buttons for the header
  const headerActions = [
    {
      id: 'toggle',
      onClick: handleToggleActive,
      className: "p-2 rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-gray-300 transition-all duration-200",
      tooltip: configuration?.is_active ? 'Deactivate configuration' : 'Activate configuration',
      customIcon: configuration?.is_active ? (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      )
    },
    {
      id: 'delete',
      onClick: handleDeleteConfiguration,
      className: "p-2 rounded-lg hover:bg-red-700/50 text-gray-400 hover:text-red-400 transition-all duration-200",
      tooltip: 'Delete configuration',
      icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
    }
  ];

  // Render content based on active tab
  const renderContent = (activeTab) => {
    if (activeTab === 'configuration') {
      return (
        <DetailsTab
          key={`config-${configuration?.job?.id}`}
          configuration={configuration}
          onUpdate={onUpdate}
          onRefreshConfiguration={onRefreshConfiguration}
          availableJobs={aiOverview?.jobs || []}
          availableConnections={aiOverview?.connections || []}
          availableProviders={aiOverview?.providers || []}
          availableModels={aiOverview?.models || []}
        />
      );
    }

    if (activeTab === 'prompt') {
      return (
        <SystemPromptTab
          key={`prompt-${configuration?.job?.id}`}
          configuration={configuration}
          onUpdate={onUpdate}
          aiOverview={aiOverview}
        />
      );
    }

    if (activeTab === 'arguments') {
      return (
        <ArgumentsTab
          key={`arguments-${configuration?.id}`}
          configuration={configuration}
          parameters={parameters}
          setParameters={setParameters}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          onUpdate={onUpdate}
        />
      );
    }

    if (activeTab === 'functions') {
      return (
        <FunctionsTab
          key={`functions-${configuration?.job?.id}`}
          configuration={configuration}
          configId={configId}
          type="functions"
          onUpdate={onUpdate}
          onRefreshConfiguration={onRefreshConfiguration}
        />
      );
    }

    return null;
  };

  return (
    <BaseDetailView
      item={configuration}
      emptyStateIcon="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      emptyStateTitle="No model selected"
      emptyStateSubtitle="Select a model from the sidebar to view details"
      headerContent={headerContent}
      headerActions={<DetailViewActionButtons actions={headerActions} />}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleSetActiveTab}
      renderContent={renderContent}
    />
  );
}
